import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MainLayout from '../components/Layout/MainLayout.js';
import FilterBar from '../components/FilterBar.js';
import BlockedOverlay from '../components/BlockedOverlay.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { useFilters } from '../context/FilterContext.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowUp, ArrowDown, Minus, Layers } from 'lucide-react';

import CompetitivenessIndex from '../components/CompetitivenessIndex.js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#2563eb', '#0f172a', '#64748b', '#94a3b8', '#3b82f6', '#1e40af'];

const PosTag = ({ diff, inverted = false }) => {
  if (diff === null || diff === undefined) return null;
  const isGood = inverted ? diff < 0 : diff > 0;
  const isBad = inverted ? diff > 0 : diff < 0;
  const Icon = isGood ? ArrowUp : isBad ? ArrowDown : Minus;
  const cls = isGood ? 'bg-emerald-100 text-emerald-700' : isBad ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{diff > 0 ? '+' : ''}{diff}%
    </span>
  );
};

const Headcount = () => {
  const [data, setData] = useState(null);
  const [byDim, setByDim] = useState(null);
  const [dim, setDim] = useState('setor');
  const { fetchWithFilters, filters } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  useEffect(() => {
    const load = async () => {
      try {
        const result = await fetchWithFilters('/hr/indicators');
        setData(result);
      } catch (e) { console.error(e); }
    };
    load();
  }, [filters, fetchWithFilters]);

  useEffect(() => {
    const loadDim = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ dim });
        ['segmento', 'regiao', 'porte'].forEach(k => { if (filters[k]) params.set(k, filters[k]); });
        const { data: r } = await axios.get(`${API}/headcount-detail/by-dimension?${params}`,
          { headers: { Authorization: `Bearer ${token}` } });
        setByDim(r);
      } catch (e) { setByDim(null); }
    };
    loadDim();
  }, [dim, filters]);

  if (!data) return <MainLayout title="Headcount" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  const blocked = data.blocked;
  const me = data.minha_empresa;
  const femPct = data.headcount_feminino?.media || 0;
  const mascPct = 100 - femPct;

  const genderPie = [
    { name: 'Feminino', value: femPct },
    { name: 'Masculino', value: mascPct },
  ];

  const regionChart = (data.by_region || []).map(r => ({
    name: r.label.charAt(0).toUpperCase() + r.label.slice(1),
    Empresas: r.n,
    Turnover: r.turnover,
    PCD: r.pcd,
  }));

  const sizeChart = (data.by_size || []).map(s => ({
    name: s.label.charAt(0).toUpperCase() + s.label.slice(1),
    Empresas: s.n,
    Turnover: s.turnover,
    'Absent.': s.absenteismo,
  }));

  return (
    <MainLayout title="Headcount" userRole={userRole}>
      <div className="space-y-6" data-testid="headcount-page">
        <FilterBar n={data.n} blocked={blocked} />

        {blocked ? <BlockedOverlay n={data.n} /> : (
          <>
            <CompetitivenessIndex variant="compact" />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <p className="text-xs text-slate-500">% Feminino (Mercado)</p>
                <p className="text-2xl font-bold text-slate-900">{femPct.toFixed(1)}%</p>
                <p className="text-xs text-slate-400">1ºQ: {data.headcount_feminino?.p25?.toFixed(1)}% | 3ºQ: {data.headcount_feminino?.p75?.toFixed(1)}%</p>
                {me?.headcount_feminino !== null && me?.headcount_feminino !== undefined && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-500">Sua: <strong>{me.headcount_feminino.toFixed(1)}%</strong></span>
                    <PosTag diff={me.headcount_feminino_diff_pct} />
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">% PCD (Mercado)</p>
                <p className="text-2xl font-bold text-slate-900">{data.pcd_percentual?.media?.toFixed(1)}%</p>
                <p className="text-xs text-slate-400">Meta legal: 5%</p>
                {me?.pcd_percentual !== null && me?.pcd_percentual !== undefined && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-500">Sua: <strong>{me.pcd_percentual.toFixed(1)}%</strong></span>
                    <PosTag diff={me.pcd_percentual_diff_pct} />
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">Satisf. (Mercado)</p>
                <p className="text-2xl font-bold text-slate-900">{data.satisfacao?.media?.toFixed(1)}%</p>
                <p className="text-xs text-slate-400">Med: {data.satisfacao?.p50?.toFixed(1)}%</p>
                {me?.satisfacao !== null && me?.satisfacao !== undefined && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-500">Sua: <strong>{me.satisfacao.toFixed(1)}%</strong></span>
                    <PosTag diff={me.satisfacao_diff_pct} />
                  </div>
                )}
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500">Treinamento (Mercado)</p>
                <p className="text-2xl font-bold text-slate-900">{data.treinamento_horas?.media?.toFixed(1)}h</p>
                <p className="text-xs text-slate-400">horas per capita</p>
                {me?.treinamento_horas !== null && me?.treinamento_horas !== undefined && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-500">Sua: <strong>{me.treinamento_horas.toFixed(1)}h</strong></span>
                    <PosTag diff={me.treinamento_horas_diff_pct} />
                  </div>
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Distribuição por Gênero</CardTitle></CardHeader>
                <CardContent className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={genderPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}>
                        {genderPie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                      </Pie>
                      <Tooltip formatter={v => `${v.toFixed(1)}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Indicadores por Região</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={regionChart} margin={{ top: 5, right: 10, bottom: 40, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="Turnover" fill="#2563eb" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="PCD" fill="#0f172a" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Indicadores por Porte</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={sizeChart} margin={{ top: 5, right: 10, bottom: 40, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="Turnover" fill="#2563eb" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Absent." fill="#64748b" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Headcount Detalhado por Dimensão */}
            <Card data-testid="headcount-detail-section">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Layers className="w-4 h-4 text-violet-600" /> Headcount Detalhado
                  </CardTitle>
                  <p className="text-[11px] text-slate-500 mt-1">Distribuição agregada do mercado por dimensão (apenas pesquisas com módulo detalhado).</p>
                </div>
                <div className="flex bg-slate-100 rounded-lg p-0.5">
                  {[{ id: 'setor', l: 'Setor' }, { id: 'cargo', l: 'Cargo' }, { id: 'tipo_servico', l: 'Tipo Serviço' }].map(d => (
                    <button key={d.id} onClick={() => setDim(d.id)} data-testid={`dim-tab-${d.id}`}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${dim === d.id ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>
                      {d.l}
                    </button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {!byDim || byDim.blocked ? (
                  <p className="text-sm text-slate-400 text-center py-6" data-testid="dim-empty">
                    {byDim?.blocked ? `Amostra insuficiente (n=${byDim.n}, mínimo ${byDim.min}).` : 'Sem dados de headcount detalhado para esta dimensão.'}
                  </p>
                ) : byDim.items.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Sem dados disponíveis.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="text-left px-2 py-2 font-medium text-slate-700">{({setor:'Setor', cargo:'Cargo', tipo_servico:'Tipo Serviço'})[byDim.dim]}</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">N empresas</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">Total Pessoas</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">% Feminino</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">% PCD</th>
                          <th className="text-right px-2 py-2 font-medium text-slate-700">Turnover</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byDim.items.map((it, i) => (
                          <tr key={i} className="border-b border-slate-100" data-testid={`dim-row-${i}`}>
                            <td className="px-2 py-1.5 font-medium text-slate-900">{it.label}</td>
                            <td className="px-2 py-1.5 text-right">{it.n_empresas}</td>
                            <td className="px-2 py-1.5 text-right">{it.total_funcionarios.toLocaleString('pt-BR')}</td>
                            <td className="px-2 py-1.5 text-right">{it.feminino_pct.toFixed(1)}%</td>
                            <td className="px-2 py-1.5 text-right">{it.pcd_pct.toFixed(1)}%</td>
                            <td className="px-2 py-1.5 text-right">{it.turnover_pct.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Headcount;
